import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { ErrorBoundary } from './components/ErrorBoundary';
// Wave 392 — Evidence Viewer modal provider (mounts once at app root).
// The modal is rendered via createPortal(document.body) inside the provider
// so it's available everywhere in the tree without prop drilling.
import { EvidenceViewerProvider } from './components/trena/EvidenceViewerContext';
// FEATURE A4 · 2026-05-21 · Global undo toast host (single slot).
import { UndoToastHost } from './components/UndoToast';
// FEATURE C2 · 2026-05-21 · auto-flush offline outbox quando voltar online.
import { useOutboxAutoflush } from './hooks/useOfflineMutation';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Wave A · Mobile resilience pass.
      // networkMode: 'always' keeps React Query attempting fetches even when
      // navigator.onLine is false — useful in poor-signal scenarios where the
      // browser under-reports connectivity. Stale cache will be served while
      // offline; the retry backoff prevents request storms.
      networkMode: 'always',
      retry: 2,
      retryDelay: (attempt: number) => Math.min(1_000 * 2 ** attempt, 8_000),
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

/** Tiny wrapper só pra montar o autoflush hook dentro do React tree. */
function AppRoot() {
  // FEATURE C2 · auto-flush quando voltar online OU quando o tab fica visível.
  useOutboxAutoflush();
  return (
    <>
      <RouterProvider router={router} />
      {/* FEATURE A4 · single-slot undo toast. Mounted aqui pra estar
          vivo independente da rota corrente. */}
      <UndoToastHost />
    </>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <EvidenceViewerProvider>
          <AppRoot />
        </EvidenceViewerProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
